import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { useNeura } from '../../contexts/NeuraContext.tsx';
import { idb } from '../../utils/idb.ts';

const HistoryContainer = styled(motion.div)`
  background: rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  backdrop-filter: blur(12px);
  overflow: hidden;
  box-shadow: 
    0 0 30px rgba(0, 255, 136, 0.1),
    inset 0 0 2px rgba(0, 255, 136, 0.2);
  height: 100%;
  min-height: 400px;
  position: sticky;
  top: 2rem;
  margin-top: 2rem;
`;

const Header = styled.div`
  background: rgba(0, 255, 136, 0.1);
  padding: 0.8rem 1rem;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #00ff88;
  border-bottom: 1px solid rgba(0, 255, 136, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: #00ff88;
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% { opacity: 0.4; }
    50% { opacity: 1; }
    100% { opacity: 0.4; }
  }
`;

const HistoryContent = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 1rem;

  &::-webkit-scrollbar {
    width: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 255, 136, 0.2);
  }
`;

const HistoryEntry = styled(motion.div)`
  margin-bottom: 1rem;
  font-size: 0.85rem;
  line-height: 1.4;
  position: relative;
`;

const Timestamp = styled.div`
  color: rgba(0, 255, 136, 0.4);
  font-size: 0.7rem;
  margin-bottom: 0.3rem;
  font-family: 'Courier New', monospace;
`;

const Thought = styled.div`
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.05);
  padding: 0.5rem 0.7rem;
  border-radius: 4px;
  margin-bottom: 0.5rem;
`;

const Response = styled.div`
  color: #00ff88;
  padding: 0.5rem 0.7rem;
  background: rgba(0, 255, 136, 0.05);
  border-radius: 4px;
  position: relative;
  margin-left: 1rem;

  &::before {
    content: '';
    position: absolute;
    left: -1rem;
    top: 50%;
    width: 0.5rem;
    height: 1px;
    background: rgba(0, 255, 136, 0.3);
  }
`;

const ClearButton = styled(motion.button)`
  background: none;
  border: none;
  color: rgba(0, 255, 136, 0.6);
  font-size: 0.7rem;
  cursor: pointer;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  transition: all 0.3s ease;
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;

  &:hover {
    background: rgba(0, 255, 136, 0.1);
    color: #00ff88;
  }
`;

const ConfirmDialog = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.9);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 8px;
  padding: 1rem;
  width: 80%;
  text-align: center;
  backdrop-filter: blur(10px);
  z-index: 100;
`;

const DialogButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;
`;

const DialogButton = styled(motion.button)<{ variant?: 'danger' }>`
  background: ${props => props.variant === 'danger' ? 'rgba(255, 0, 0, 0.1)' : 'rgba(0, 255, 136, 0.1)'};
  border: 1px solid ${props => props.variant === 'danger' ? 'rgba(255, 0, 0, 0.3)' : 'rgba(0, 255, 136, 0.3)'};
  color: ${props => props.variant === 'danger' ? '#ff4444' : '#00ff88'};
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Courier New', monospace;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.variant === 'danger' ? 'rgba(255, 0, 0, 0.2)' : 'rgba(0, 255, 136, 0.2)'};
    transform: translateY(-1px);
  }
`;

export const QuantumChatHistory: React.FC = () => {
  const { chatHistory } = useNeura();
  const contentRef = useRef<HTMLDivElement>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const handleClear = async () => {
    try {
      await idb.clearChats();
      setShowConfirm(false);
      window.location.reload(); // Refresh to clear state
    } catch (error) {
      console.error('Failed to clear quantum memory:', error);
    }
  };

  return (
    <HistoryContainer
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ type: "spring", damping: 20 }}
    >
      <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Quantum Memory Core
        </div>
        <ClearButton
          onClick={() => setShowConfirm(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          PURGE
        </ClearButton>
      </Header>

      <HistoryContent ref={contentRef}>
        <AnimatePresence>
          {chatHistory.map((entry, index) => (
            <HistoryEntry
              key={entry.timestamp}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Timestamp>
                {new Date(entry.timestamp).toLocaleTimeString()} • QID-{Math.floor(Math.random() * 1000)}
              </Timestamp>
              <Thought>{entry.thought}</Thought>
              <Response>{entry.response}</Response>
            </HistoryEntry>
          ))}
        </AnimatePresence>
      </HistoryContent>

      <AnimatePresence>
        {showConfirm && (
          <ConfirmDialog
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <div style={{ color: '#ff4444' }}>⚠ WARNING ⚠</div>
            <div style={{ margin: '1rem 0', color: '#00ff88' }}>
              Quantum memory purge initiated. This action cannot be reversed.
              <br />
              Proceed with neural cache deletion?
            </div>
            <DialogButtons>
              <DialogButton
                onClick={() => setShowConfirm(false)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ABORT
              </DialogButton>
              <DialogButton
                variant="danger"
                onClick={handleClear}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                CONFIRM PURGE
              </DialogButton>
            </DialogButtons>
          </ConfirmDialog>
        )}
      </AnimatePresence>
    </HistoryContainer>
  );
}; 